import EditableInput from "@/components/ui/editable-input";
import { CreditCard, Building2, User, Edit, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef, useEffect } from "react";

interface BankAccountData {
  iban: string;
  account_holder: string;
  bank_name: string;
  swift_bic: string;
}

interface BankAccountSectionProps {
  bankData: BankAccountData;
  onBankDataChange: (data: BankAccountData) => void;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const BankAccountSection = ({ 
  bankData, 
  onBankDataChange, 
  isEditing = true,
  onEditToggle,
  onCancelEdit,
  onSave,
  isSaving = false
}: BankAccountSectionProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleInputChange = (field: keyof BankAccountData) => (value: string) => {
    onBankDataChange({
      ...bankData,
      [field]: value
    });
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && onSave) {
      onSave();
    }
  };

  // Gestione tasto Invio
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isEditing && onSave) {
        e.preventDefault();
        onSave();
      }
    };

    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, onSave]);

  return (
    <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <CreditCard className="h-5 w-5" />
            Informazioni Bancarie
          </h3>
          <p className="text-sm text-gray-600">
            Inserisci le informazioni del tuo conto corrente per ricevere i pagamenti
          </p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2 min-h-[40px] items-center">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancelEdit}
                  className="flex items-center gap-2"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Annulla
                </Button>
                {onSave && (
                  <Button
                    type="submit"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isSaving ? 'Salvando...' : 'Salva'}
                  </Button>
                )}
              </>
            ) : (
              <Button
                type="button"
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* IBAN */}
      <EditableInput
        label="IBAN *"
        value={bankData.iban}
        icon={CreditCard}
        isEditing={isEditing}
        onChange={(value) => handleInputChange('iban')(value.toUpperCase())}
        placeholder="IT60 X054 2811 1010 0000 0123 456"
        maxLength={34}
        className="font-mono text-sm"
      />
      {isEditing && (
        <p className="text-sm text-gray-500">
          Inserisci l'IBAN senza spazi (verranno aggiunti automaticamente)
        </p>
      )}

      {/* Account Holder */}
      <EditableInput
        label="Intestatario Conto *"
        value={bankData.account_holder}
        icon={User}
        isEditing={isEditing}
        onChange={handleInputChange('account_holder')}
        placeholder="Nome e Cognome dell'intestatario"
      />

      {/* Bank Name */}
      <EditableInput
        label="Nome Banca *"
        value={bankData.bank_name}
        icon={Building2}
        isEditing={isEditing}
        onChange={handleInputChange('bank_name')}
        placeholder="Nome della banca"
      />

      {/* SWIFT/BIC */}
      <EditableInput
        label="SWIFT/BIC"
        value={bankData.swift_bic}
        isEditing={isEditing}
        onChange={(value) => handleInputChange('swift_bic')(value.toUpperCase())}
        placeholder="BCITITMMXXX"
        maxLength={11}
        className="font-mono text-sm"
      />
      {isEditing && (
        <p className="text-sm text-gray-500">
          Codice SWIFT/BIC della banca (opzionale)
        </p>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-blue-900">
              Informazioni Sicure
            </h4>
            <p className="text-sm text-blue-700">
              Le tue informazioni bancarie sono protette e utilizzate solo per i pagamenti. 
              Non vengono mai condivise con terze parti.
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default BankAccountSection; 